import { Camera } from 'three';
import {
  Burner,
  Color,
  Cupboard,
  Gas,
  RangeHeight,
  Line,
  RangeModel,
  Oven,
  RangeTop,
  Spacer,
  Trim,
} from './Enums.js';
import { ampsLookup } from './rangeLookups.js';
import { Url } from './Url.js';

/**
 * The configured state of the Lacanche range. This includes the chosen line,
 * range top, trim, color, and optional burners
 */
export class RangeState {
  // This is the initial state of the Lacanche range

  /**
   * Whether a range is selected or not
   * @type {boolean}
   */
  isSelected = true;

  /**
   * The model of the range
   * @type {string}
   */
  model = RangeModel.sully;

  /**
   * The line of the range (classique or moderne)
   * @type {string}
   */
  line = Line.classique;

  /**
   * The style of the range top (classique, traditional, 4-feux, or classique-reverse)
   * @type {string}
   */
  top = RangeTop.classique;

  /**
   * The color of the range (too many to list)
   * @type {string}
   */
  color = Color.matteBlack;

  /**
   * The trim of the range (brass, brushedSS, chrome, or nickel)
   * @type {string}
   */
  trim = Trim.brass;

  /**
   * The type of left oven (gas, electric, or electric convection)
   * @type {?string}
   */
  leftOven = Oven.gas;

  /**
   * The type of left oven (gas, electric, or electric convection)
   * @type {?string}
   */
  rightOven = Oven.convection;

  /**
   * The gas type of the range
   * @type {string}
   */
  gas = Gas.notSure;

  /**
   * The height of the range
   * @type {string}
   */
  height = RangeHeight.notSure;

  /**
   * The type of left cupboard (warming or storage)
   * @type {?string}
   */
  leftCupboard = null;

  /**
   * The type of right cupboard (warming or storage)
   * @type {?string}
   */
  rightCupboard = null;

  /**
   * The first (leftmost) optional "burner" for the range top
   * @type {?string}
   */
  option1 = null;

  /**
   * The second optional "burner" for the range top
   * @type {?string}
   */
  option2 = null;

  /**
   * The third optional "burner" for the range top (only available on Sully 1800 and 2200)
   * @type {?string}
   */
  option3 = null;

  /**
   * The fourth optional "burner" for the range top (only available on Sully 2200)
   * @type {?string}
   */
  option4 = null;

  /**
   * The size of small broiling oven (grande or petite)
   * @type {?string}
   */
  petiteOven = null;

  /**
   * The position of the back spacer (raised or flush)
   * @type {string}
   */
  spacer = Spacer.flush;

  /**
   * The number of storage drawers (0, 1, or 2)
   * @type {number}
   */
  storageDrawers = 2;

  /**
   * Should the labels for the various range parts be displayed?
   * @type {boolean}
   */
  showLabels = false;

  /**
   * Is this range created from a configuration URL?
   * @type {boolean}
   */
  isConfigured = false;

  /**
   * The camera used to view the scene
   * @type {Camera}
   */
  camera;

  /**
   * When over 0, the manually specified range voltage
   * @type {number}
   */
  overrideVolts = 0;

  /**
   * The URL object used to manage the configuration URL
   * @type {Url}
   */
  #url;

  /**
   * Create a new state object
   * @param {Url} url
   */
  constructor(url) {
    this.#url = url;
  }

  /**
   * Maximum range amperage draw.
   * @returns {number}
   */
  rangeAmps() {
    let amps =
      ampsLookup[this.leftCupboard] +
      ampsLookup[this.petiteOven] +
      ampsLookup[this.leftOven] +
      ampsLookup[this.rightOven] +
      ampsLookup[this.rightCupboard] +
      ampsLookup[this.option1] +
      ampsLookup[this.option2] +
      ampsLookup[this.option3] +
      ampsLookup[this.option4];

    // For ranges with only gas, show a little bit of current draw
    if (amps === 0) amps = 0.1;

    // Round to 1 decimal place
    return Math.round(amps * 10) / 10;
  }

  /**
   * The range voltage.
   * @returns {number}
   */
  rangeVolts() {
    if (this.overrideVolts > 0) {
      return this.overrideVolts;
    }

    let volts = this.rangeAmps() < 0.5 ? 120 : 240;

    if (this.#url.voltageOverride() === '240') {
      this.overrideVolts = 240;
      volts = 240;
    }

    return volts;
  }

  /**
   * Set up the state of the range from a previously configured range
   *
   * @param {object} configuredRange
   * @param {string} configuredRange.model
   * @param {string} configuredRange.line
   * @param {string} configuredRange.top
   * @param {string} configuredRange.color
   * @param {string} configuredRange.trim
   * @param {string} configuredRange.leftOven
   * @param {string} configuredRange.rightOven
   * @param {string} configuredRange.gas
   * @param {string} configuredRange.height
   * @param {string} configuredRange.leftCupboard
   * @param {string} configuredRange.rightCupboard
   * @param {string} configuredRange.option1
   * @param {string} configuredRange.option2
   * @param {string} configuredRange.option3
   * @param {string} configuredRange.option4
   */
  setState(configuredRange) {
    this.isConfigured = true;
    this.isSelected = true;

    const cupboardsAndVertOven = Object.values(Cupboard).concat(Oven.vertConv);

    if (Object.values(RangeModel).includes(configuredRange.model)) {
      this.model = configuredRange.model;
    } else {
      this.model = RangeModel.sully;
      return;
    }

    if (Object.values(Line).includes(configuredRange.line)) {
      this.line = configuredRange.line;
    }

    if (Object.values(RangeTop).includes(configuredRange.top)) {
      this.top = configuredRange.top;
    }

    if (Object.values(Trim).includes(configuredRange.trim)) {
      this.trim = configuredRange.trim;
    }

    if (Object.values(Color).includes(configuredRange.color)) {
      this.color = configuredRange.color;
    }

    if (Object.values(Oven).includes(configuredRange.leftOven)) {
      this.leftOven = configuredRange.leftOven;
    }

    if (Object.values(Oven).includes(configuredRange.rightOven)) {
      this.rightOven = configuredRange.rightOven;
    }

    if (Object.values(Gas).includes(configuredRange.gas)) {
      this.gas = configuredRange.gas;
    }

    if (Object.values(RangeHeight).includes(configuredRange.height)) {
      this.height = configuredRange.height;
    }

    if (cupboardsAndVertOven.includes(configuredRange.leftCupboard)) {
      this.leftCupboard = configuredRange.leftCupboard;
    }

    if (cupboardsAndVertOven.includes(configuredRange.rightCupboard)) {
      this.rightCupboard = configuredRange.rightCupboard;
    }

    if (Object.values(Burner).includes(configuredRange.option1)) {
      this.option1 = configuredRange.option1;
    }

    if (Object.values(Burner).includes(configuredRange.option2)) {
      this.option2 = configuredRange.option2;
    }

    if (Object.values(Burner).includes(configuredRange.option3)) {
      this.option3 = configuredRange.option3;
    }

    if (Object.values(Burner).includes(configuredRange.option4)) {
      this.option4 = configuredRange.option4;
    }
  }

  /**
   * The slug for a fully configured range.
   * @returns {string}
   */
  slug() {
    let slug =
      this.model +
      '-' +
      this.line +
      '-' +
      this.top +
      '-' +
      this.color +
      '-' +
      this.trim +
      '-' +
      this.leftOven +
      '-' +
      this.rightOven +
      '-' +
      this.gas +
      '-' +
      this.height +
      '-' +
      this.leftCupboard +
      '-' +
      this.rightCupboard +
      '-' +
      this.option1 +
      '-' +
      this.option2 +
      '-' +
      this.option3 +
      '-' +
      this.option4;

    return slug.replace(/null/g, '').replace(/-+$/, '');
  }

  /**
   * The URL query parameter for the override voltage.
   * @returns {string}
   */
  voltsParam() {
    return this.overrideVolts > 0 ? `&volts=${this.overrideVolts}` : '';
  }
}
